import React, { useState, useEffect } from 'react';
import http from '../../api/http';
import { Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { timeAgo } from '../../components/layouts/mainLayout/HeaderRight';
import CloseIcon from '@mui/icons-material/Close';

interface AuditLog {
  _id: string;
  timestamp: string;
  action: string;
  resource: string;
  userId: string;
  userRole: string;
  group: string;
  status: 'success' | 'fail';
  detail: any;
  ip: string;
  before?: any;
  after?: any;
  userAgent?: string;
  createdAt: string;
  updatedAt?: string;
  userEmail?: string;
  userName?: string;
  resourceId?: string;
}

interface AuditResponse {
  success: boolean;
  data: {
    logs: AuditLog[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface AuditStats {
  totalLogs: number;
  logsToday: number;
  logsThisWeek: number;
  logsThisMonth: number;
  topActions: Array<{ action: string; count: number }>;
  topResources: Array<{ resource: string; count: number }>;
  topGroups: Array<{ group: string; count: number }>;
  topUserRoles: Array<{ userRole: string; count: number }>;
  statusStats: Array<{ status: string; count: number }>;
}

const AuditPage = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [daysToKeep, setDaysToKeep] = useState(90);
  const [exportFilters, setExportFilters] = useState({
    resource: '',
    action: '',
    from: '',
    to: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    resource: '',
    action: '',
    userId: '',
    userRole: '',
    group: '',
    status: '',
    from: '',
    to: '',
    sort: 'desc'
  });

  useEffect(() => {
    fetchAuditLogs();
    fetchStats();
  }, [filters, pagination.page, pagination.limit]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.resource) params.append('resource', filters.resource);
      if (filters.action) params.append('action', filters.action);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.userRole) params.append('userRole', filters.userRole);
      if (filters.group) params.append('group', filters.group);
      if (filters.status) params.append('status', filters.status);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());
      params.append('sort', filters.sort);

      const response = await http.get(`/api/audit-logs?${params.toString()}`);
      const data: AuditResponse = response.data;
      
      if (data.success && data.data) {
        setAuditLogs(data.data.logs || []);
        setPagination({
          total: data.data.total,
          page: data.data.page,
          limit: data.data.limit,
          totalPages: data.data.totalPages
        });
      } else {
        setAuditLogs([]);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await http.get('/api/audit-logs/stats');
      const data = response.data;
      if (data && data.success && data.data) {
        setStats(data.data);
      } else {
        setStats(null);
      }
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
      setStats(null);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      if (exportFilters.resource) params.append('resource', exportFilters.resource);
      if (exportFilters.action) params.append('action', exportFilters.action);
      if (exportFilters.from) params.append('from', exportFilters.from);
      if (exportFilters.to) params.append('to', exportFilters.to);

      const response = await http.get(`/api/audit-logs/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setExportDialogOpen(false);
      alert('Export completed successfully!');
    } catch (error) {
      console.error('Failed to export audit logs:', error);
      alert('Failed to export audit logs. Please try again.');
    }
  };

  const handleCleanup = async () => {
    try {
      const response = await http.post('/api/audit-logs/cleanup', {
        daysToKeep: daysToKeep
      });
      
      if (response.data.success) {
        alert(`Successfully deleted ${response.data.data.deletedCount} old audit logs`);
        setCleanupDialogOpen(false);
        fetchAuditLogs();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to cleanup audit logs:', error);
      alert('Failed to cleanup audit logs. Please try again.');
    }
  };

  const handleViewDetails = async (logId: string) => {
    setDetailLoading(true);
    setDetailDialogOpen(true);
    try {
      const response = await http.get(`/api/audit-logs/${logId}`);
      if (response.data.success && response.data.data) {
        setSelectedLog(response.data.data);
      } else {
        setSelectedLog(null);
      }
    } catch (error) {
      console.error('Failed to fetch audit log details:', error);
      setSelectedLog(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredLogs = auditLogs;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Audit Logs
      </Typography>

      {stats && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Audit Statistics
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Logs</Typography>
              <Typography variant="h6">{stats.totalLogs}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Today</Typography>
              <Typography variant="h6">{stats.logsToday}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">This Week</Typography>
              <Typography variant="h6">{stats.logsThisWeek}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">This Month</Typography>
              <Typography variant="h6">{stats.logsThisMonth}</Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Top Actions
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {stats.topActions.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2">{item.action}</Typography>
                    <Typography variant="body2" color="primary">{item.count}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Top Resources
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {stats.topResources.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', maxWidth: '70%' }}>
                      {item.resource}
                    </Typography>
                    <Typography variant="body2" color="primary">{item.count}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {stats.topGroups && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Top Groups
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {stats.topGroups.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2">{item.group}</Typography>
                      <Typography variant="body2" color="primary">{item.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {stats.topUserRoles && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Top User Roles
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {stats.topUserRoles.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2">{item.userRole}</Typography>
                      <Typography variant="body2" color="primary">{item.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {stats.statusStats && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Status Statistics
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {stats.statusStats.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ 
                        display: 'inline-block', 
                        px: 1, 
                        py: 0.25, 
                        borderRadius: 0.5, 
                        bgcolor: item.status === 'success' ? 'success.light' : 'error.light',
                        color: item.status === 'success' ? 'success.contrastText' : 'error.contrastText',
                        fontSize: '0.75rem'
                      }}>
                        {item.status}
                      </Box>
                      <Typography variant="body2" color="primary">{item.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current View
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Logs</Typography>
            <Typography variant="h6">{pagination.total}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Current Page</Typography>
            <Typography variant="h6">{pagination.page} / {pagination.totalPages}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Logs per Page</Typography>
            <Typography variant="h6">{pagination.limit}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Showing</Typography>
            <Typography variant="h6">{auditLogs.length} logs</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
          <TextField
            label="Resource"
            value={filters.resource}
            onChange={(e) => setFilters({ ...filters, resource: e.target.value })}
            size="small"
            placeholder="Biomarker, User, Disease"
          />
          <TextField
            label="Action"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            size="small"
            placeholder="CREATE_USER, UPDATE_DISEASE"
          />
          <TextField
            label="User ID"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            size="small"
          />
          <TextField
            label="User Role"
            value={filters.userRole}
            onChange={(e) => setFilters({ ...filters, userRole: e.target.value })}
            size="small"
            placeholder="admin, user, moderator"
          />
          <FormControl size="small">
            <InputLabel>Group</InputLabel>
            <Select
              value={filters.group}
              onChange={(e) => setFilters({ ...filters, group: e.target.value })}
              label="Group"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Data Processing">Data Processing</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="fail">Fail</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="From Date"
            type="datetime-local"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="datetime-local"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small">
            <InputLabel>Sort Order</InputLabel>
            <Select
              value={filters.sort}
              onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
              label="Sort Order"
            >
              <MenuItem value="desc">Descending</MenuItem>
              <MenuItem value="asc">Ascending</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => {
            setExportFilters({
              resource: filters.resource,
              action: filters.action,
              from: filters.from,
              to: filters.to
            });
            setExportDialogOpen(true);
          }}>
            Export
          </Button>
          <Button variant="outlined" color="warning" onClick={() => setCleanupDialogOpen(true)}>
            Cleanup Old Logs
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => {
              setFilters({
                resource: '',
                action: '',
                userId: '',
                userRole: '',
                group: '',
                status: '',
                from: '',
                to: '',
                sort: 'desc'
              });
              setPagination({ ...pagination, page: 1 });
            }}
          >
            Reset Filters
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
                      <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>User Role</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {filteredLogs.length > 0 ? (
              filteredLogs.map((log) => (
                <TableRow key={log._id || Math.random()}>
                  <TableCell>{timeAgo(log.timestamp)}</TableCell>
                  <TableCell>{log.action || '-'}</TableCell>
                  <TableCell>{log.userId || '-'}</TableCell>
                  <TableCell>{log.userRole || '-'}</TableCell>
                  <TableCell>{log.group || '-'}</TableCell>
                  <TableCell>{log.resource || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'inline-block', 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1, 
                      bgcolor: log.status === 'success' ? 'success.light' : 'error.light',
                      color: log.status === 'success' ? 'success.contrastText' : 'error.contrastText',
                      fontSize: '0.75rem'
                    }}>
                      {log.status || '-'}
                    </Box>
                  </TableCell>
                  <TableCell>{log.ip || '-'}</TableCell>
                  <TableCell>
                    <pre style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {JSON.stringify(log.detail || {}, null, 2)}
                    </pre>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewDetails(log._id)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {loading ? 'Loading...' : 'No audit logs found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      {pagination.totalPages > 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total logs)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={pagination.page <= 1}
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Audit Log Details</Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>Loading details...</Typography>
            </Box>
          ) : selectedLog ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                  <Typography variant="body1">{selectedLog._id}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                  <Typography variant="body1">{timeAgo(selectedLog.timestamp)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                  <Typography variant="body1">{selectedLog.action}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
                  <Typography variant="body1">{selectedLog.userId}</Typography>
                </Box>
                {selectedLog.userName && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">User Name</Typography>
                    <Typography variant="body1">{selectedLog.userName}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">User Role</Typography>
                  <Typography variant="body1">{selectedLog.userRole}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Group</Typography>
                  <Typography variant="body1">{selectedLog.group}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Resource</Typography>
                  <Typography variant="body1">{selectedLog.resource}</Typography>
                </Box>
                {selectedLog.resourceId && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Resource ID</Typography>
                    <Typography variant="body1">{selectedLog.resourceId}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Box sx={{ 
                    display: 'inline-block', 
                    px: 1, 
                    py: 0.5, 
                    borderRadius: 1, 
                    bgcolor: selectedLog.status === 'success' ? 'success.light' : 'error.light',
                    color: selectedLog.status === 'success' ? 'success.contrastText' : 'error.contrastText',
                    fontSize: '0.75rem'
                  }}>
                    {selectedLog.status}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                  <Typography variant="body1">{selectedLog.ip}</Typography>
                </Box>
                {selectedLog.userAgent && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">User Agent</Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                      {selectedLog.userAgent}
                    </Typography>
                  </Box>
                )}
                {selectedLog.createdAt && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                    <Typography variant="body1">{timeAgo(selectedLog.createdAt)}</Typography>
                  </Box>
                )}
                {selectedLog.updatedAt && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                    <Typography variant="body1">{timeAgo(selectedLog.updatedAt)}</Typography>
                  </Box>
                )}
              </Box>
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Detail</Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                  {JSON.stringify(selectedLog.detail, null, 2)}
                </pre>
              </Paper>

              {selectedLog.before && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Before</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2, opacity: 0.8 }}>
                    <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                      {JSON.stringify(selectedLog.before, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}

              {selectedLog.after && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>After</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2, opacity: 0.8 }}>
                    <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                      {JSON.stringify(selectedLog.after, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography color="error">Failed to load audit log details</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cleanupDialogOpen}
        onClose={() => setCleanupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Cleanup Old Audit Logs</Typography>
            <IconButton onClick={() => setCleanupDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This action will permanently delete audit logs older than the specified number of days. This action cannot be undone.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Days to Keep"
              type="number"
              value={daysToKeep}
              onChange={(e) => setDaysToKeep(parseInt(e.target.value) || 90)}
              fullWidth
              helperText="Audit logs older than this many days will be deleted"
              inputProps={{ min: 1, max: 3650 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCleanup} 
            color="warning" 
            variant="contained"
          >
            Cleanup
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Export Audit Logs</Typography>
            <IconButton onClick={() => setExportDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Configure export filters. The export will include all audit logs matching the specified criteria.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Resource"
              value={exportFilters.resource}
              onChange={(e) => setExportFilters({ ...exportFilters, resource: e.target.value })}
              placeholder="Biomarker, User, Disease"
              fullWidth
            />
            <TextField
              label="Action"
              value={exportFilters.action}
              onChange={(e) => setExportFilters({ ...exportFilters, action: e.target.value })}
              placeholder="CREATE_USER, UPDATE_DISEASE"
              fullWidth
            />
            <TextField
              label="From Date"
              type="datetime-local"
              value={exportFilters.from}
              onChange={(e) => setExportFilters({ ...exportFilters, from: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To Date"
              type="datetime-local"
              value={exportFilters.to}
              onChange={(e) => setExportFilters({ ...exportFilters, to: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleExport} 
            color="primary" 
            variant="contained"
          >
            Export CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditPage;
